import { IconToggle } from '@modules/common/components/IconToggle/IconToggle';
import { trackSearchSummaryEvent } from '@modules/feed/utils/trackSearchSummaryEvents';
import { FeedbackListSummaryHeader } from '@modules/oracle/components/FeedbackListSummary/FeedbackListSummaryHeader';
import { FeedbackListSummaryFeedbackButtons } from '@modules/oracle/components/FeedbackListSummaryFeedbackButtons';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { useCopySummaryAsFormattedText } from '@modules/oracle/hooks';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { CheckRounded, ContentPasteOutlined } from '@mui/icons-material';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { useState } from 'react';
import { QueryContainer } from 'v2/core/services/notebook/types';

interface SearchSummaryHeaderProps {
  queryContainer: QueryContainer;
  hasNoSummary?: boolean;
  responseID?: string;
}

const SummaryCopiedToggledIcon = () => {
  return (
    <Tooltip title="Copied">
      <IconButton aria-label="copied" size="small">
        <CheckRounded sx={{ color: 'text.secondary' }} fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
};

export const SearchSummaryHeader = ({
  queryContainer,
  hasNoSummary,
  responseID,
}: SearchSummaryHeaderProps) => {
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const rootID = useQueryBuilderStore(({ query }) => query.rootID);

  const messages = useFeedbackSummaryStore(({ data }) => data.messages);

  const copySummaryAsFormattedText = useCopySummaryAsFormattedText({
    messages,
    queryContainer,
    nodes,
    rootID,
  });

  const [shouldToggleCopySummaryIcon, setShouldToggleCopySummaryIcon] =
    useState(false);

  const copySummary = async () => {
    const unformattedSummaryString = await copySummaryAsFormattedText();
    setShouldToggleCopySummaryIcon(true);
    trackSearchSummaryEvent({
      eventName: 'Search Summary - Copy Summary',
      copiedSummaryText: unformattedSummaryString,
    });
  };

  const renderCopySummaryDefaultIcon = () => {
    return (
      <Tooltip title="Copy Summary">
        <IconButton
          aria-label="copy summary"
          onClick={copySummary}
          size="small"
        >
          <ContentPasteOutlined
            sx={{ color: 'text.secondary' }}
            fontSize="inherit"
          />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Stack
      justifyContent="space-between"
      alignItems="center"
      direction="row"
      width="100%"
    >
      <FeedbackListSummaryHeader hasNoSummary={hasNoSummary} />
      <Stack direction="row" spacing={1} alignItems="center">
        {!hasNoSummary && (
          <IconToggle
            defaultIcon={renderCopySummaryDefaultIcon()}
            toggledIcon={<SummaryCopiedToggledIcon />}
            isToggled={shouldToggleCopySummaryIcon}
            onToggle={setShouldToggleCopySummaryIcon}
          />
        )}
        {responseID && (
          <FeedbackListSummaryFeedbackButtons responseID={responseID} />
        )}
      </Stack>
    </Stack>
  );
};
